import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Calendar, Tag, Phone } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import api from "../lib/api";
import logger from "../lib/logger";

const NewsArticlePage = () => {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchArticle = async () => {
      try {
        const data = await api.getNewsArticle(slug);
        setArticle(data);
      } catch (error) {
        logger.error("Error fetching article:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchArticle();
  }, [slug]);

  const categoryLabels = {
    landlord_tips: "Landlord Tips",
    tenant_advice: "Tenant Advice",
    market_insights: "Market Insights",
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="animate-pulse">
          <div className="h-8 bg-slate-200 rounded w-32 mb-8" />
          <div className="h-12 bg-slate-200 rounded w-3/4 mb-4" />
          <div className="h-6 bg-slate-200 rounded w-1/2 mb-8" />
          <div className="h-64 bg-slate-200 rounded-lg mb-8" />
          <div className="space-y-4">
            <div className="h-4 bg-slate-200 rounded w-full" />
            <div className="h-4 bg-slate-200 rounded w-5/6" />
            <div className="h-4 bg-slate-200 rounded w-4/5" />
          </div>
        </div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
        <h1 className="font-heading text-2xl font-semibold text-navy mb-4">Article Not Found</h1>
        <p className="text-slate-600 mb-6">The article you're looking for doesn't exist or has been removed.</p>
        <Button asChild className="bg-navy hover:bg-navy-light">
          <Link to="/news">View All Articles</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="py-8 md:py-12" data-testid="news-article-page">
      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Button */}
        <Link
          to="/news"
          className="inline-flex items-center text-navy hover:text-gold mb-8 font-medium"
          data-testid="back-to-news"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to News
        </Link>

        {/* Article Header */}
        <header className="mb-8">
          <Badge variant="secondary" className="mb-4 bg-navy/5 text-navy">
            <Tag className="w-3 h-3 mr-1" />
            {categoryLabels[article.category] || article.category}
          </Badge>
          <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-semibold text-navy mb-4 leading-tight">
            {article.title}
          </h1>
          <div className="flex items-center gap-4 text-slate-500">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              <span>{formatDate(article.created_at)}</span>
            </div>
          </div>
        </header>

        {/* Featured Image */}
        {article.image_url && (
          <div className="mb-8 rounded-lg overflow-hidden">
            <img
              src={article.image_url}
              alt={article.title}
              className="w-full h-auto"
            />
          </div>
        )}

        {/* Article Content */}
        <div className="prose prose-slate prose-lg max-w-none
                        prose-headings:font-heading prose-headings:text-navy prose-headings:font-semibold
                        prose-h1:text-3xl prose-h1:mt-10 prose-h1:mb-4
                        prose-h2:text-2xl prose-h2:mt-8 prose-h2:mb-3
                        prose-h3:text-xl prose-h3:mt-6 prose-h3:mb-2
                        prose-p:text-slate-700 prose-p:leading-relaxed
                        prose-strong:text-navy prose-strong:font-semibold
                        prose-a:text-gold prose-a:no-underline hover:prose-a:underline
                        prose-ul:my-4 prose-ol:my-4
                        prose-li:text-slate-700 prose-li:my-1
                        prose-blockquote:border-l-gold prose-blockquote:text-slate-600
                        prose-img:rounded-lg prose-img:my-6">
          <p className="text-lg md:text-xl text-slate-600 mb-8 leading-relaxed font-light">
            {article.excerpt}
          </p>
          <ReactMarkdown remarkPlugins={[remarkGfm]}>
            {article.content || ""}
          </ReactMarkdown>
        </div>

        {/* CTA */}
        <div className="mt-12 p-8 bg-navy rounded-lg text-white text-center">
          <h3 className="font-heading text-2xl font-semibold mb-4">
            Need Property Management Help?
          </h3>
          <p className="text-white/80 mb-6">
            Contact Keystone Properties for professional property management across West Yorkshire.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              asChild
              className="bg-gold hover:bg-gold-light text-navy"
            >
              <Link to="/contact">Get in Touch</Link>
            </Button>
            <Button
              asChild
              variant="outline"
              className="border-white text-white hover:bg-white hover:text-navy"
            >
              <a href="tel:01924 520163">
                <Phone className="w-4 h-4 mr-2" />
                01924 520163
              </a>
            </Button>
          </div>
        </div>
      </article>
    </div>
  );
};

export default NewsArticlePage;
